import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Grid, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment/moment';
import { LoadingBar, TableForm } from '../Commons';
import cm from '../../helpers/commons';
import exportedJsonData from './sidkaeGrandDeductDetail.json';

const TableWrapper = styled.div`
  position: relative;
  height: 345px;
  overflow-y: scroll;
  border: 1px solid #dcdcdc;

  table.ui.table {
    border: none;
    thead {
      position: sticky;
      top: 0;
      tr,
      th {
        border: none !important;
      }
    }
  }
`;

const userInfoColumns = [
  [
    { header: '사용자 명', name: 'name' },
    { header: '사용자 ID', name: 'signId' },
    { header: '부서', name: 'orgCode' }
  ],
  [
    { header: '사원 번호', name: 'comIdNum' },
    { header: '직위', name: 'position' },
    { header: '직책', name: 'rankPosition' }
  ]
];
const searchOptionColumns = [
  { header: '포인트 정보', name: 'pointType' },
  { header: '시작 기간', name: 'startDate' },
  { header: '종료 기간', name: 'endDate' }
];
const taskDetailColumns = [
  { header: '날짜', name: 'useDate', textAlign: 'left' },
  { header: '사용자명', name: 'userName', textAlign: 'left' },
  { header: '정책그룹명', name: 'mealGroupName', textAlign: 'left' },
  { header: '정책명', name: 'mealPolicyName', textAlign: 'left' },
  { header: '타입', name: 'logType', textAlign: 'left' },
  { header: '지급사유', name: 'cause', textAlign: 'left' },
  { header: '만료일시', name: 'expireDate', textAlign: 'left' },
  { header: '금액', name: 'amount', textAlign: 'right' }
];

const generateGridColumns = (columns, value) => (
  <Grid.Row key={columns[0].name}>
    {columns.map(({ header, name }) => {
      const data = name === 'orgCode' ? cm.divisionNavi(value[name]) : value[name];
      return (
        <React.Fragment key={name}>
          <Grid.Column width={2} className="summary-hd" style={{ padding: '11px 10px 9px', textAlign: 'center' }}>
            {header}
          </Grid.Column>
          <Grid.Column style={{ padding: '11px 10px 9px', textAlign: 'center' }}>{data || '-'}</Grid.Column>
        </React.Fragment>
      );
    })}
  </Grid.Row>
);

const generateColumns = (values) => {
  const thead = taskDetailColumns.map((item) => ({ data: item.header, ...item }));
  const tbody = values.map((value) => {
    const division = cm.trTdDivision(value.orgCode);
    const row = taskDetailColumns.map((item) => {
      let data = value[item.name] || '-';
      let fontColor = '';

      if (item.header.includes('부서')) {
        const idx = Number(item.header.replace('부서', '')) - 1;
        data = division[idx] || '-';
      } else if (item.name === 'amount') {
        data = value[item.name].toLocaleString() || 0;
        fontColor = value[item.name] < 0 && 'red';
      } else if (item.name === 'useDate') {
        const days = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' };
        const day = moment(value[item.name]).day();
        data = moment(value[item.name]).format(`YYYY-MM-DD (${days[day]}) HH:mm:ss`);
      } else if (item.name === 'expireDate') {
        data = value[item.name] ? `${moment(value[item.name]).format('YYYY-MM-DD HH:mm:ss')}` : '-';
      }
      return { data, ...item, fontColor };
    });
    return { row };
  });
  return { thead, tbody: values ? tbody : undefined };
};

const UserSikdaeGrandDeductDetailModal = ({ open, close, date, sikdaeGrandDeductDetail, searchDate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(false);

  const grandDeductDetail = useMemo(() => {
    setIsLoading(true);
    const { content = [], user = {}, sumAmount = 0, totalRow = 0 } = sikdaeGrandDeductDetail.data || {};
    if (sikdaeGrandDeductDetail.data && sikdaeGrandDeductDetail.data.content) {
      setIsLoading(false);
    }
    return { content, user, sumAmount, totalRow };
  }, [sikdaeGrandDeductDetail]);

  const gridData = useMemo(() => {
    return {
      ...grandDeductDetail.user,
      pointType: '식대 포인트',
      startDate: searchDate ? moment(searchDate.startDate).format('YYYY-MM-DD') : '-',
      endDate: searchDate ? moment(searchDate.endDate).format('YYYY-MM-DD') : '-'
    };
  }, [searchDate, grandDeductDetail]);

  const handleExcelDownload = async () => {
    setIsDisable(true);
    const newRows = grandDeductDetail.content.map((item) => {
      const days = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' };
      const day = moment(item.useDate).day();
      const useDate = moment(item.useDate).format(`YYYY-MM-DD (${days[day]}) HH:mm:ss`);
      const expireDate = item.expireDate ? `${moment(item.expireDate).format('YYYY-MM-DD HH:mm:ss')}` : '-';
      return [
        '',
        useDate,
        item.userName,
        item.mealGroupName,
        item.mealPolicyName,
        item.logType,
        item.cause,
        expireDate,
        item.amount
      ];
    });
    const apiExcelDatas = {
      0: {
        customValues: [
          // 사용자 정보
          { address: 'C3', value: gridData.name },
          { address: 'E3', value: gridData.userId },
          { address: 'G3', value: cm.divisionNavi(gridData.orgCode) },
          { address: 'C4', value: gridData.comIdNum || '-' },
          { address: 'E4', value: gridData.position || '-' },
          { address: 'G4', value: gridData.rankPosition || '-' },
          // 검색 조건
          { address: 'C7', value: gridData.pointType },
          { address: 'E7', value: moment(gridData.startDate).format('YYYY-MM-DD') },
          { address: 'G7', value: moment(gridData.endDate).format('YYYY-MM-DD') }
        ],
        startRowNum: 10,
        newRows
      }
    };
    const fileName = `사용자별 식대 지급/차감 내역(상세)_${moment().valueOf()}`;
    await handleFileExport(exportedJsonData, apiExcelDatas, fileName);
    setTimeout(() => {
      setIsDisable(() => false);
    }, 1000);
  };

  const tableData = useMemo(() => {
    const { thead, tbody } = generateColumns(grandDeductDetail.content);
    const sub = [
      { data: '합계', colSpan: thead.length - 1, textAlign: 'left' },
      { data: (grandDeductDetail.sumAmount || 0).toLocaleString(), textAlign: 'right' }
    ];
    return { header: { main: thead, sub }, body: { main: tbody } };
  }, [grandDeductDetail, open]);

  return (
    <Modal className="modal-large" open={open} onClose={close} style={{ width: 950 }}>
      {isLoading ? <LoadingBar /> : null}
      <Modal.Header style={{ justifyContent: 'space-between', padding: '12px 20px' }}>
        <div>사용자별 지급/차감 내역(상세)</div>
        <Button
          color="green"
          inverted
          style={{ padding: '10px 20px 9px' }}
          onClick={handleExcelDownload}
          disabled={isDisable}
        >
          엑셀 다운로드
        </Button>
      </Modal.Header>
      <Modal.Content>
        <div style={{ padding: '16px 0px 4px', fontWeight: 700 }}>사용자 정보 (현재)</div>
        <Grid celled columns="equal" className="modal-summary">
          {userInfoColumns.map((columns) => generateGridColumns(columns, gridData))}
        </Grid>
        <div style={{ padding: '16px 0px 4px', fontWeight: 700 }}>검색 조건</div>
        <Grid celled columns="equal" className="modal-summary">
          {generateGridColumns(searchOptionColumns, gridData)}
        </Grid>
        {/* Table */}
        <div style={{ padding: '8px 0px', fontWeight: 700 }}>검색 결과 : {grandDeductDetail.totalRow.toLocaleString()}건</div>
        <TableWrapper className="table-box">
          <TableForm tabledata={tableData} />
        </TableWrapper>
      </Modal.Content>
      <Modal.Actions style={{ padding: '12px 20px' }}>
        <Button color="green" content="확인" style={{ padding: '10px 20px 9px' }} onClick={close} />
      </Modal.Actions>
    </Modal>
  );
};

export default connect((state) => ({
  date: state.commons.date,
  sikdaeGrandDeductDetail: state.usage.userSikdaeGrandDeductDetail || {}
}))(UserSikdaeGrandDeductDetailModal);
